/**
 * Select Component
 * Encapsulated Select with consistent styling
 */

import React from 'react';
import { 
  Select as MuiSelect, 
  SelectProps as MuiSelectProps,
  FormControl,
  FormControlProps,
  InputLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

export interface SelectProps extends Omit<MuiSelectProps, 'size'> {
  formControlProps?: Omit<FormControlProps, 'sx'> & { sx?: object };
  label?: string;
  size?: 'small' | 'medium';
}

export const Select: React.FC<SelectProps> = ({ 
  sx,
  formControlProps,
  size,
  label,
  ...props 
}) => {
  const theme = useTheme();
  
  const defaultSelectStyles = {
    backgroundColor: theme.palette.mode === 'dark' ? theme.customColors.searchBackground : 'transparent',
    color: theme.palette.text.primary,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.customColors.searchBorder,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '& .MuiSelect-icon': {
      color: theme.palette.text.secondary,
    },
  };

  const defaultFormControlStyles = {
    '& .MuiOutlinedInput-root': {
      borderColor: theme.customColors.searchBorder,
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
  };

  const { sx: formControlSx, ...restFormControlProps } = formControlProps || {};

  return (
    <FormControl
      size={size}
      {...restFormControlProps}
      sx={{
        ...defaultFormControlStyles,
        ...formControlSx,
      }}
    >
      {label && (
        <InputLabel
          sx={{
            color: theme.palette.text.secondary,
            '&.Mui-focused': {
              color: theme.palette.primary.main,
            },
          }}
        >
          {label}
        </InputLabel>
      )}
      <MuiSelect
        size={size}
        label={label}
        sx={{
          ...defaultSelectStyles,
          ...sx,
        }}
        {...props}
      />
    </FormControl>
  );
};

